import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  DollarSign,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Lock,
  ShieldCheck,
  AlertTriangle,
  FileCheck,
  Search,
  Filter,
  TrendingUp,
  User,
  Info,
  Printer
} from 'lucide-react';
import { UserSession } from '../types';
import {
  getCurrentPayPeriod,
  getAdjacentPayPeriod,
  getRecentPayPeriods,
  formatDisplayDate,
  AllegiantPayPeriod,
  getPayPeriodForDate
} from '../utils/payPeriod';
import { clientNcnsEncounterLabel } from '../utils/clientNcnsLabel';

const OPEN_PERIOD_COMPENSATION_NOTE = 'Compensation is based on average qualifying weekly sessions across the two-week pay period. Final earnings are available after payroll is finalized.';

interface MyPayViewProps {
  user: UserSession;
  onSelectClient?: (clientId: string) => void;
}

interface ServicePayRecord {
  id: string;
  docId: string | null;
  docType: string | null;
  dos: string;
  weekNumber: number;
  clientId: string;
  clientName: string;
  ahcccsId: string;
  serviceCode: string;
  serviceType: string;
  modality: string;
  plannedDurationMinutes: number;
  actualDurationMinutes: number;
  isProrated: boolean;
  baseSessionRate: number;
  rateSource: string;
  displayRateSource: string;
  proratedPay: number | null;
  manualAdjustment: number;
  calculatedTotal: number | null;
  clearanceStatus: 'CLEARED' | 'PENDING_SUPERVISOR' | 'PENDING_GUARDIAN' | 'PENDING_CLINICIAN' | 'PENDING_DOCUMENTATION' | 'NEEDS_CORRECTION' | 'COMPLIANCE_RATE' | 'LATE_SERVICE_REVIEW' | 'FINALIZED';
  clearanceLabel: string;
  documentationEligible?: boolean;
  documentationHoldReason?: string | null;
  completionNotes?: string;
  status: string;
  isFrozen: boolean;
  isLateException: boolean;
  lateExceptionReason?: string;
  isAmbiguous: boolean;
  ambiguityReason?: string;
  isComplianceOverride?: boolean;
  isUnder53Minutes?: boolean;
  isLateGuardianSignature?: boolean;
  ownerDocumentationExceptionReason?: string | null;
  billingDispositionReason?: string | null;
  carriedFromEarlierPeriod?: boolean;
  heldFromPeriodLabel?: string | null;
  heldFromLabel?: string | null;
  isClientNcns?: boolean;
  ncnsSequence?: number;
}

interface MyPayResponse {
  payPeriod: {
    id: string;
    startDate: string;
    endDate: string;
    cutoffDate: string;
    processingDate: string;
    payDate: string;
    label: string;
    isCurrent: boolean;
    isFinalized: boolean;
    finalizedAt: string | null;
    status: 'FINALIZED' | 'OPEN';
    payStatusLabel: string;
    previousStartDate: string;
    nextStartDate: string;
    canNavigateNext: boolean;
    weeks: {
      week1: { weekNumber: number; startDate: string; endDate: string; label: string };
      week2: { weekNumber: number; startDate: string; endDate: string; label: string };
    };
  };
  provider: {
    id: string;
    name: string;
    role: string;
    title: string;
    payLadder?: 'STANDARD' | 'CF_PLUS_5';
  };
  summary: {
    completedServiceCount: number;
    week1OnsiteTreatmentCount: number;
    week1Rate: number;
    week1NextTier?: {
      nextTierCount: number | null;
      sessionsToNextTier: number | null;
      nextTierRate: number | null;
    } | null;
    week2OnsiteTreatmentCount: number;
    week2Rate: number;
    week2NextTier?: {
      nextTierCount: number | null;
      sessionsToNextTier: number | null;
      nextTierRate: number | null;
    } | null;
    ratePerSession: number | null;
    ratePerEval: number | null;
    totalPlannedMinutes: number;
    totalActualMinutes: number;
    totalProratedPay: number;
    totalManualAdjustments: number;
    calculatedTotalPay: number;
    lateExceptionsCount: number;
  };
  services: ServicePayRecord[];
}

function localIsoDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const MyPayView: React.FC<MyPayViewProps> = ({ user, onSelectClient }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unlinkedProfile, setUnlinkedProfile] = useState(false);
  const [data, setData] = useState<MyPayResponse | null>(null);

  // Selected Pay Period state
  const [selectedStartDate, setSelectedStartDate] = useState<string>(() => getCurrentPayPeriod().startDate);
  const [awaitingDefault, setAwaitingDefault] = useState(true);
  const loadedStartRef = useRef<string | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [ncnsOpen, setNcnsOpen] = useState(false);
  const [ncnsClientId, setNcnsClientId] = useState('');
  const [ncnsDos, setNcnsDos] = useState(localIsoDate);
  const [ncnsSaving, setNcnsSaving] = useState(false);
  const [ncnsError, setNcnsError] = useState('');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWeekFilter, setSelectedWeekFilter] = useState<'ALL' | 'WEEK1' | 'WEEK2'>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const isSupervisor = user.role === 'SUPERVISOR';
  const isAssistant = user.role === 'ASSISTANT';
  const isClinical = isSupervisor || isAssistant;

  const recentPeriods = useMemo(() => getRecentPayPeriods(6, 4), []);

  useEffect(() => {
    fetch('/api/assignments?activeOnly=1')
      .then((res) => (res.ok ? res.json() : []))
      .then((arr) => {
        if (Array.isArray(arr)) {
          setAssignments(arr);
        }
      })
      .catch((e) => console.warn('[MyPay] Could not fetch assignments:', e));
  }, []);

  const fetchMyPay = async (startDateToFetch: string) => {
    setLoading(true);
    setData(null);
    setError(null);
    setUnlinkedProfile(false);
    try {
      const res = await fetch(`/api/payroll/my-pay?startDate=${startDateToFetch}`);
      if (res.status === 404) {
        const json = await res.json().catch(() => ({}));
        if (json.error === 'PROVIDER_PROFILE_NOT_LINKED') {
          setUnlinkedProfile(true);
          return;
        }
      }
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `Server responded with status ${res.status}`);
      }
      const json: MyPayResponse = await res.json();
      loadedStartRef.current = json.payPeriod.startDate;
      setData(json);
      setSelectedStartDate(json.payPeriod.startDate);
    } catch (err: any) {
      console.error('[MyPay] Error fetching provider payroll:', err);
      setError(err.message || 'Failed to load payroll information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const resolveDefaultPeriod = async () => {
      const current = getCurrentPayPeriod();
      const candidates = [0, -1, -2, -3, -4, -5, -6].map((offset) =>
        offset === 0 ? current : getAdjacentPayPeriod(current, offset)
      );
      try {
        for (const period of candidates) {
          const res = await fetch(`/api/payroll/my-pay?startDate=${encodeURIComponent(period.startDate)}`);
          if (cancelled) return;
          if (res.status === 404) {
            const json = await res.json().catch(() => ({}));
            if (json.error === 'PROVIDER_PROFILE_NOT_LINKED') {
              setUnlinkedProfile(true);
              setLoading(false);
              setAwaitingDefault(false);
              return;
            }
          }
          if (!res.ok) {
            const errJson = await res.json().catch(() => ({}));
            throw new Error(errJson.message || `Server responded with status ${res.status}`);
          }
          const json: MyPayResponse = await res.json();
          if (json.payPeriod?.status === 'FINALIZED') {
            if (cancelled) return;
            loadedStartRef.current = json.payPeriod.startDate;
            setData(json);
            setSelectedStartDate(json.payPeriod.startDate);
            setLoading(false);
            setAwaitingDefault(false);
            return;
          }
        }
        if (!cancelled) setAwaitingDefault(false);
      } catch (err: any) {
        if (cancelled) return;
        console.error('[MyPay] Error resolving finalized payroll:', err);
        setError(err.message || 'Failed to load payroll information');
        setLoading(false);
        setAwaitingDefault(false);
      }
    };
    resolveDefaultPeriod();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (awaitingDefault) return;
    if (loadedStartRef.current === selectedStartDate) return;
    fetchMyPay(selectedStartDate);
  }, [selectedStartDate, awaitingDefault]);

  const ncnsClients = useMemo(() => {
    const providerId = data?.provider?.id;
    if (!providerId) return [];
    const seen = new Set<string>();
    return assignments.filter((assignment) => {
      if (assignment.provider_id !== providerId || assignment.active !== 1 || !assignment.client_id) return false;
      if (seen.has(assignment.client_id)) return false;
      seen.add(assignment.client_id);
      return true;
    });
  }, [assignments, data?.provider?.id]);

  const openClientNcns = () => {
    setNcnsError('');
    setNcnsDos(localIsoDate());
    setNcnsClientId('');
    setNcnsOpen(true);
  };

  const submitClientNcns = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ncnsClientId || !ncnsDos || ncnsSaving) return;
    setNcnsSaving(true);
    setNcnsError('');
    try {
      const res = await fetch('/api/payroll/client-no-show', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: ncnsClientId, dos: ncnsDos })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'The client no-show could not be recorded.');
      }
      const period = getPayPeriodForDate(ncnsDos);
      setNcnsOpen(false);
      if (period.startDate === selectedStartDate) {
        await fetchMyPay(period.startDate);
      } else {
        setSelectedStartDate(period.startDate);
      }
    } catch (err: any) {
      setNcnsError(err.message || 'The client no-show could not be recorded.');
    } finally {
      setNcnsSaving(false);
    }
  };

  const handlePrevPeriod = () => {
    if (data?.payPeriod?.previousStartDate) {
      setSelectedStartDate(data.payPeriod.previousStartDate);
    }
  };

  const handleNextPeriod = () => {
    if (data?.payPeriod?.nextStartDate) {
      setSelectedStartDate(data.payPeriod.nextStartDate);
    }
  };

  const handleCurrentPeriod = () => {
    const current = getCurrentPayPeriod();
    setSelectedStartDate(current.startDate);
  };

  // Filtered services
  const filteredServices = useMemo(() => {
    if (!data?.services) return [];
    return data.services.filter((s) => {
      // Week filter
      if (selectedWeekFilter === 'WEEK1' && s.weekNumber !== 1) return false;
      if (selectedWeekFilter === 'WEEK2' && s.weekNumber !== 2) return false;

      // Status filter. A no-show is not a SOAP clearance state.
      if (statusFilter !== 'ALL') {
        if (s.isClientNcns) return false;
        if (statusFilter === 'CLEARED' && s.clearanceStatus !== 'CLEARED' && s.clearanceStatus !== 'FINALIZED') return false;
        if (statusFilter === 'PENDING_SUPERVISOR' && s.clearanceStatus !== 'PENDING_SUPERVISOR') return false;
        if (statusFilter === 'PENDING_GUARDIAN' && s.clearanceStatus !== 'PENDING_GUARDIAN') return false;
        if (statusFilter === 'COMPLIANCE' && !s.isComplianceOverride && s.clearanceStatus !== 'COMPLIANCE_RATE') return false;
        if (statusFilter === 'LATE' && !s.isLateException) return false;
      }

      // Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesClient = s.clientName.toLowerCase().includes(q) || s.ahcccsId.toLowerCase().includes(q);
        const matchesCode = s.serviceCode.toLowerCase().includes(q) || s.serviceType.toLowerCase().includes(q);
        const matchesDos = s.dos.includes(q);
        if (!matchesClient && !matchesCode && !matchesDos) return false;
      }

      return true;
    });
  }, [data?.services, selectedWeekFilter, statusFilter, searchQuery]);

  // Counts by week & clearance for header pills
  const { week1Count, week2Count, clearedCount, pendingCount } = useMemo(() => {
    if (!data?.services) return { week1Count: 0, week2Count: 0, clearedCount: 0, pendingCount: 0 };
    let w1 = 0;
    let w2 = 0;
    let clr = 0;
    let pnd = 0;
    for (const s of data.services) {
      if (s.weekNumber === 1) w1++;
      if (s.weekNumber === 2) w2++;
      if (s.isClientNcns) continue;
      if (s.clearanceStatus === 'CLEARED' || s.clearanceStatus === 'FINALIZED') clr++;
      else pnd++;
    }
    return { week1Count: w1, week2Count: w2, clearedCount: clr, pendingCount: pnd };
  }, [data?.services]);

  // Clearance badge renderer
  const renderClearanceBadge = (srv: ServicePayRecord) => {
    if (srv.isLateException) {
      return (
        <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
          isClinical
            ? 'bg-purple-50 text-purple-800 border border-purple-200'
            : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
        }`}>
          <AlertTriangle className={`w-3 h-3 ${isClinical ? 'text-purple-600' : 'text-purple-400'}`} />
          <span>Late Service — Review</span>
        </span>
      );
    }
    if (srv.documentationEligible === false && !srv.isLateException) {
      const label = srv.clearanceLabel || 'Documentation pending';
      return (
        <span
          className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
            isClinical
              ? 'bg-cyan-50 text-cyan-800 border border-cyan-200'
              : 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30'
          }`}
          title={srv.documentationHoldReason || label}
        >
          <Clock className={`w-3 h-3 ${isClinical ? 'text-cyan-600' : 'text-indigo-400'}`} />
          <span>{label}</span>
        </span>
      );
    }
    if (srv.isFrozen) {
      return (
        <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
          isClinical
            ? 'bg-slate-100 text-slate-700 border border-slate-200'
            : 'bg-slate-800 text-slate-300 border border-slate-700'
        }`}>
          <Lock className={`w-3 h-3 ${isClinical ? 'text-slate-500' : 'text-slate-400'}`} />
          <span>Finalized</span>
        </span>
      );
    }
    if (srv.rateSource === 'Owner non-billable — $25') {
      return (
        <span
          className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
            isClinical
              ? 'bg-slate-100 text-slate-800 border border-slate-300'
              : 'bg-slate-700 text-slate-100 border border-slate-500'
          }`}
          title={srv.billingDispositionReason || srv.rateSource}
        >
          <Info className={`w-3 h-3 ${isClinical ? 'text-slate-600' : 'text-slate-300'}`} />
          <span>Owner non-billable ($25)</span>
        </span>
      );
    }
    if (srv.rateSource === 'Owner documentation exception — $25') {
      return (
        <span
          className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
            isClinical
              ? 'bg-sky-50 text-sky-900 border border-sky-200'
              : 'bg-sky-500/15 text-sky-200 border border-sky-500/30'
          }`}
          title={srv.ownerDocumentationExceptionReason || srv.rateSource}
        >
          <Info className={`w-3 h-3 ${isClinical ? 'text-sky-700' : 'text-sky-300'}`} />
          <span>Owner exception ($25)</span>
        </span>
      );
    }
    if (srv.isComplianceOverride || srv.clearanceStatus === 'COMPLIANCE_RATE') {
      return (
        <span
          className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
            isClinical
              ? 'bg-amber-50 text-amber-800 border border-amber-200'
              : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
          }`}
          title={srv.isUnder53Minutes ? 'Under 53-minute compliance floor ($25)' : 'Late guardian signature compliance rate ($25)'}
        >
          <Info className={`w-3 h-3 ${isClinical ? 'text-amber-600' : 'text-amber-400'}`} />
          <span>Compliance Rate ($25)</span>
        </span>
      );
    }
    if (srv.clearanceStatus === 'PENDING_SUPERVISOR') {
      return (
        <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
          isClinical
            ? 'bg-cyan-50 text-cyan-800 border border-cyan-200'
            : 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30'
        }`}>
          <Clock className={`w-3 h-3 ${isClinical ? 'text-cyan-600' : 'text-indigo-400'}`} />
          <span>Pending Supervisor</span>
        </span>
      );
    }
    if (srv.clearanceStatus === 'PENDING_GUARDIAN') {
      return (
        <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
          isClinical
            ? 'bg-amber-50 text-amber-800 border border-amber-200'
            : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
        }`}>
          <Clock className={`w-3 h-3 ${isClinical ? 'text-amber-600' : 'text-amber-400'}`} />
          <span>Pending Guardian</span>
        </span>
      );
    }
    if (srv.clearanceStatus === 'NEEDS_CORRECTION') {
      return (
        <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
          isClinical
            ? 'bg-rose-50 text-rose-800 border border-rose-200'
            : 'bg-rose-500/15 text-rose-300 border border-rose-500/30'
        }`}>
          <AlertTriangle className={`w-3 h-3 ${isClinical ? 'text-rose-600' : 'text-rose-400'}`} />
          <span>Needs Correction</span>
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
        isClinical
          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
          : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
      }`}>
        <FileCheck className={`w-3 h-3 ${isClinical ? 'text-emerald-600' : 'text-emerald-400'}`} />
        <span>Cleared</span>
      </span>
    );
  };

  if (unlinkedProfile) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 md:p-8 text-slate-200">
        <div className="max-w-3xl mx-auto mt-12 bg-slate-900/90 border border-slate-800 rounded-2xl p-8 text-center shadow-xl">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-4">
            <User className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Provider Profile Not Linked</h2>
          <p className="text-slate-400 text-sm max-w-md mx-auto mb-6 leading-relaxed">
            Your user account (<strong>{user.name}</strong> • {user.role}) is not currently linked to an active clinical provider profile in the Allegiant roster.
          </p>
          <div className="bg-slate-950/80 border border-slate-800/80 rounded-xl p-4 text-xs text-left text-slate-400 space-y-2 max-w-lg mx-auto">
            <div className="font-semibold text-slate-300 flex items-center space-x-2">
              <Info className="w-4 h-4 text-sky-400" />
              <span>How to resolve:</span>
            </div>
            <p>1. Contact an Allegiant Clinical Administrator or Practice Manager.</p>
            <p>2. Ask them to verify your provider record in <strong>Providers & Supervision</strong> and ensure your email ({user.email || 'account'}) matches your provider roster entry.</p>
          </div>
        </div>
      </div>
    );
  }

  const isFinalized = data?.payPeriod?.status === 'FINALIZED';

  return (
    <div className="space-y-4 pb-16 text-slate-200">
      {/* Top Header & Context Bar — Canonical Steel-to-Navy Shell on mobile, Canonical Rich Navy on desktop/tablet */}
      <div className="border border-slate-700/80 rounded-xl px-4 py-2.5 sm:rounded-2xl sm:px-6 sm:py-4 relative overflow-hidden mobile-header-shell sm:desktop-header-shell shadow-md sm:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
        <div className="sm:hidden absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent pointer-events-none" />
        <div className="hidden sm:block absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
        <div className="hidden sm:block absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-emerald-500/5 via-teal-500/5 to-transparent pointer-events-none" />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
          {/* Mobile Title (sm:hidden) — Centered Title Cluster with Money Icon */}
          <div className="sm:hidden flex items-center justify-center space-x-2 py-0.5 w-full">
            <div className="p-1 rounded-lg border shrink-0 bg-emerald-500/20 border-emerald-500/40 text-emerald-400 shadow-sm">
              <DollarSign className="w-3.5 h-3.5" />
            </div>
            <h1 className="text-base font-bold tracking-tight text-white">
              My Pay
            </h1>
          </div>

          {/* Desktop/Tablet Left Title (hidden sm:flex) - HARD FROZEN */}
          <div className="hidden sm:flex items-center space-x-2.5 shrink-0">
            <div className={`p-1.5 rounded-lg border text-emerald-400 ${
              isSupervisor
                ? 'bg-emerald-950/40 border-emerald-500/25 shadow-none'
                : 'bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border-emerald-500/40 shadow-sm emerald-glow-subtle'
            }`}>
              <DollarSign className="w-4 h-4" />
            </div>
            <h1 className="text-base font-bold text-white tracking-tight flex items-center space-x-2">
              <span>My Pay</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-semibold ${
                isSupervisor
                  ? 'bg-emerald-950/60 text-emerald-300/90 border border-emerald-500/20 shadow-none'
                  : 'bg-emerald-950/70 text-emerald-300 border border-emerald-500/30 shadow-[0_0_8px_rgba(16,185,129,0.15)]'
              }`}>
                Provider Earnings
              </span>
            </h1>
          </div>

          {/* Mobile Pay Period Controls (sm:hidden) */}
          <div className="sm:hidden flex flex-col gap-2 w-full pt-1">
            {/* ROW 1: [ previous ] [ 09/12/2026 – 09/25/2026 ] [ next ] */}
            <div className="flex items-center rounded-lg p-0.5 space-x-1 w-full min-w-0 bg-white border border-slate-200/90 shadow-xs">
              <button
                onClick={handlePrevPeriod}
                disabled={awaitingDefault}
                className="p-1.5 rounded-md transition-colors cursor-pointer shrink-0 hover:bg-slate-50 text-slate-500 hover:text-slate-800 disabled:text-slate-300 disabled:cursor-not-allowed"
                title="Previous Pay Period"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <select
                value={selectedStartDate}
                disabled={awaitingDefault}
                onChange={(e) => setSelectedStartDate(e.target.value)}
                className="text-[11px] rounded px-2 py-1 font-mono focus:outline-none cursor-pointer flex-1 min-w-0 bg-white border border-slate-200 text-slate-800 focus:border-slate-400"
              >
                {recentPeriods.map((p) => (
                  <option key={p.id} value={p.startDate}>
                    {p.label}
                  </option>
                ))}
              </select>

              <button
                onClick={handleNextPeriod}
                disabled={awaitingDefault || Boolean(data && !data.payPeriod.canNavigateNext)}
                className={`p-1.5 rounded-md transition-colors shrink-0 ${
                  data && !data.payPeriod.canNavigateNext
                    ? 'text-slate-300 cursor-not-allowed'
                    : 'hover:bg-slate-50 text-slate-500 hover:text-slate-800 cursor-pointer'
                }`}
                title="Next Pay Period"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* ROW 2: [ Current Period ] [ Print Statement ] */}
            <div className="grid grid-cols-2 gap-2 w-full">
              <button
                onClick={handleCurrentPeriod}
                disabled={awaitingDefault}
                className={`w-full py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all cursor-pointer border text-center disabled:opacity-60 ${
                  data?.payPeriod?.isCurrent
                    ? 'bg-slate-100 text-slate-700 border-slate-300 shadow-2xs'
                    : isAssistant
                    ? 'bg-cyan-50 text-cyan-800 border-cyan-200 hover:bg-cyan-100 shadow-2xs'
                    : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 shadow-2xs'
                }`}
              >
                Current Period
              </button>

              <button
                onClick={() => window.print()}
                className="w-full flex items-center justify-center space-x-1.5 py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all cursor-pointer border bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200 shadow-2xs"
                title="Print or export statement as PDF"
              >
                <Printer className="w-3 h-3 text-slate-500" />
                <span>Print Statement</span>
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Pay Period Controls (hidden sm:flex) - HARD FROZEN */}
          <div className="hidden sm:flex flex-wrap items-center gap-1.5 sm:gap-2">
            <div className="flex items-center bg-white border border-slate-200/90 rounded-lg p-0.5 space-x-0.5 shadow-xs">
              <button
                onClick={handlePrevPeriod}
                disabled={awaitingDefault}
                className="p-1 rounded-md hover:bg-slate-50 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer disabled:text-slate-300 disabled:cursor-not-allowed"
                title="Previous Pay Period"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              <select
                value={selectedStartDate}
                disabled={awaitingDefault}
                onChange={(e) => setSelectedStartDate(e.target.value)}
                className="bg-white border border-slate-200 text-[11px] text-slate-800 rounded px-2 py-1 font-mono focus:outline-none focus:border-slate-400 cursor-pointer"
              >
                {recentPeriods.map((p) => (
                  <option key={p.id} value={p.startDate}>
                    {p.label} {p.isCurrent ? '(Current)' : ''}
                  </option>
                ))}
              </select>

              <button
                onClick={handleNextPeriod}
                disabled={awaitingDefault || Boolean(data && !data.payPeriod.canNavigateNext)}
                className={`p-1 rounded-md transition-colors ${
                  data && !data.payPeriod.canNavigateNext
                    ? 'text-slate-300 cursor-not-allowed'
                    : 'hover:bg-slate-50 text-slate-500 hover:text-slate-800 cursor-pointer'
                }`}
                title="Next Pay Period"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <button
              onClick={handleCurrentPeriod}
              disabled={awaitingDefault}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all cursor-pointer border shadow-xs disabled:opacity-60 ${
                data?.payPeriod?.isCurrent
                  ? 'bg-slate-100 text-slate-700 border-slate-300'
                  : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
              }`}
            >
              Current Period
            </button>

            <button
              onClick={() => window.print()}
              className="flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 transition-all cursor-pointer shadow-xs"
              title="Print or export statement as PDF"
            >
              <Printer className="w-3 h-3 text-slate-500" />
              <span>Print Statement</span>
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Error Alert */}
        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 flex items-center space-x-2.5 text-rose-300 text-xs">
            <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading && !data && (
          <div className={`p-12 text-center text-xs ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
            <p>Loading encounter payroll details...</p>
          </div>
        )}

        {/* Period Timetable & Status Strip — Compact Polished Glass */}
        {data && (
          <div className={`border rounded-xl px-3.5 py-2 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm relative overflow-hidden ${
            isClinical
              ? 'bg-white border-slate-200/90 sm:bg-gradient-to-r sm:from-slate-900/95 sm:via-[#0b1219]/90 sm:to-slate-950/95 sm:border-emerald-500/25'
              : 'bg-gradient-to-r from-slate-900/95 via-[#0b1219]/90 to-slate-950/95 border-emerald-500/25'
          }`}>
            {!isAssistant && (
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent pointer-events-none" />
            )}
            {/* Mobile Top Row: Centered Expected Pay title, followed by centered Cycle row */}
            <div className="sm:hidden flex flex-col items-center justify-center gap-1.5 w-full">
              {/* Centered Pay Status Badge */}
              {isFinalized ? (
                <div className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-lg ${
                  isClinical
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs'
                    : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-[0_0_8px_rgba(16,185,129,0.2)]'
                }`}>
                  <ShieldCheck className={`w-3.5 h-3.5 ${isAssistant ? 'text-emerald-600' : 'text-emerald-400'}`} />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    Finalized Pay (Official Locked Snapshot)
                  </span>
                </div>
              ) : (
                <div className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-lg ${
                  isClinical
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs'
                    : 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/40 shadow-[0_0_8px_rgba(16,185,129,0.2)] shimmer-emerald'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isAssistant ? 'bg-emerald-600' : 'bg-emerald-400 animate-pulse'}`} />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    Open period
                  </span>
                </div>
              )}

              <div className={`text-[11px] text-center ${isAssistant ? 'text-slate-500' : 'text-slate-400'}`}>
                <span>Cycle: </span>
                <strong className={`font-mono ${isAssistant ? 'text-slate-900' : 'text-white'}`}>{data.payPeriod.label}</strong>
              </div>
            </div>

            {/* Desktop Top Row (hidden sm:flex) - HARD FROZEN */}
            <div className="hidden sm:flex flex-wrap items-center gap-2">
              {/* Pay Status Badge */}
              {isFinalized ? (
                <div className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-lg ${
                  isSupervisor
                    ? 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/25 shadow-none'
                    : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-[0_0_8px_rgba(16,185,129,0.2)]'
                }`}>
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    Finalized Pay (Official Locked Snapshot)
                  </span>
                </div>
              ) : (
                <div className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-lg ${
                  isSupervisor
                    ? 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/25 shadow-none'
                    : 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/40 shadow-[0_0_8px_rgba(16,185,129,0.2)] shimmer-emerald'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full bg-emerald-400 ${isSupervisor ? '' : 'animate-pulse'}`} />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    Open period
                  </span>
                </div>
              )}

              <div className="text-[11px] text-slate-400">
                <span>Cycle: </span>
                <strong className="text-white font-mono">{data.payPeriod.label}</strong>
              </div>
            </div>

            {/* Mobile Timetable row: Processing + Payout on ONE row, Cutoff removed (sm:hidden) */}
            <div className={`sm:hidden flex items-center justify-between gap-1.5 text-[10.5px] font-mono w-full pt-1 border-t ${
              isClinical ? 'border-slate-200' : 'border-slate-800/60'
            }`}>
              <span className={`px-2 py-0.5 rounded-md border ${
                isClinical ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-slate-950/90 border-slate-800 text-slate-400'
              }`}>
                Processing <strong className={isAssistant ? 'text-sky-700 font-semibold' : 'text-sky-300'}>{formatDisplayDate(data.payPeriod.processingDate)} (Tue)</strong>
              </span>
              <span className={`px-2 py-0.5 rounded-md border ${
                isClinical ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-emerald-950/50 border border-emerald-500/30 text-emerald-300'
              }`}>
                Payout <strong className={isAssistant ? 'text-emerald-900 font-semibold' : 'text-emerald-200'}>{formatDisplayDate(data.payPeriod.payDate)} (Wed)</strong>
              </span>
            </div>

            {/* Desktop Timetable (hidden sm:flex) - HARD FROZEN */}
            <div className="hidden sm:flex flex-wrap items-center gap-1.5 text-[11px] font-mono">
              <span className="px-2 py-0.5 rounded-md bg-slate-950/90 border border-slate-800 text-slate-400 shadow-inner">
                Cutoff: <strong className="text-amber-300">{formatDisplayDate(data.payPeriod.cutoffDate)} (Fri)</strong>
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-950/90 border border-slate-800 text-slate-400 shadow-inner">
                Processing: <strong className="text-sky-300">{formatDisplayDate(data.payPeriod.processingDate)} (Tue)</strong>
              </span>
              <span className={`px-2 py-0.5 rounded-md text-emerald-300 ${
                isSupervisor
                  ? 'bg-emerald-950/40 border border-emerald-500/20 shadow-none'
                  : 'bg-emerald-950/50 border border-emerald-500/30 shadow-[0_0_6px_rgba(16,185,129,0.15)]'
              }`}>
                Payout: <strong className="text-emerald-200">{formatDisplayDate(data.payPeriod.payDate)} (Wed)</strong>
              </span>
            </div>
          </div>
        )}

        {/* Informational Banner: Open vs Finalized — Slimmer Dark Glass */}
        {data && (
          <div>
            {isFinalized ? (
              <div className={`rounded-xl px-3.5 py-2 flex items-center space-x-2.5 text-xs shadow-sm ${
                isClinical
                  ? 'bg-white border border-emerald-200/90'
                  : 'bg-gradient-to-r from-emerald-950/30 via-slate-900/50 to-slate-950/60 border border-emerald-500/25 text-emerald-200/90'
              }`}>
                <Lock className={`w-3.5 h-3.5 shrink-0 ${isClinical ? 'text-emerald-700' : 'text-emerald-400'}`} />
                <p className={`text-[11px] leading-snug ${isClinical ? 'text-slate-800' : 'text-emerald-200/80'}`}>
                  <strong className={`font-semibold mr-1.5 ${isClinical ? 'text-emerald-800' : 'text-emerald-300'}`}>This pay period is Finalized:</strong>
                  The gross pay shown is the official locked payout for this cycle.
                </p>
              </div>
            ) : (
              <div className={`rounded-xl px-3.5 py-2 flex items-center space-x-2.5 text-xs shadow-sm ${
                isClinical
                  ? 'bg-white border border-emerald-200/90'
                  : 'bg-gradient-to-r from-slate-900/70 via-emerald-950/20 to-slate-950/70 border border-emerald-500/25 text-slate-300'
              }`}>
                <Info className={`w-3.5 h-3.5 shrink-0 ${isClinical ? 'text-emerald-700' : 'text-emerald-400'}`} />
                <p className={`text-[11px] leading-snug ${isClinical ? 'text-slate-800' : 'text-slate-300'}`}>
                  {OPEN_PERIOD_COMPENSATION_NOTE}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Late Encounter Alert Banner if any */}
        {data && data.summary.lateExceptionsCount > 0 && (
          <div className="bg-purple-950/20 border border-purple-800/40 rounded-xl p-4 flex items-start space-x-3 text-purple-200">
            <AlertTriangle className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
            <div className="text-xs space-y-1.5 flex-1">
              <div className="font-bold text-purple-200 flex items-center space-x-2">
                <span>Late Encounter Exception Detected ({data.summary.lateExceptionsCount})</span>
                <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-purple-950/80 border border-purple-500/30 text-purple-300">
                  Admin Review Required
                </span>
              </div>
              <p className="text-purple-300/90 leading-relaxed text-[11px]">
                One or more encounters in this cycle occurred in a finalized pay period but were submitted after payroll cutoff. These encounters are marked for administrative payroll resolution so company records remain balanced.
              </p>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TOP KPI SECTION: REFINED HIERARCHY (Compressed Primary Heroes + Ledger)   */}
        {/* ========================================================================= */}
        {data && (
          <div className="space-y-2.5">
            {/* Primary Heroes Grid: Finalized Gross Pay and Completed Services */}
            <div className={`grid grid-cols-1 gap-3 ${isFinalized ? 'md:grid-cols-2' : ''}`}>
              {isFinalized && (
              <div className={`rounded-xl px-4 py-3 relative overflow-hidden transition-all ${
                isClinical
                  ? 'bg-white border border-slate-200/90 shadow-2xs'
                  : 'bg-gradient-to-br from-slate-900/95 via-emerald-950/25 to-slate-950/95 border border-emerald-500/35 hover:border-emerald-500/50 emerald-glow-subtle shadow-black/40'
              }`}>
                {!isClinical && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-600" />
                    <div className="absolute top-0 right-0 w-28 h-28 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />
                  </>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`p-1 rounded-md ${
                      isClinical
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                        : 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
                    }`}>
                      <DollarSign className="w-3.5 h-3.5" />
                    </div>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${
                      isClinical ? 'text-slate-900' : 'text-emerald-300'
                    }`}>
                      Finalized Gross Pay
                    </span>
                  </div>
                  <span className={`text-[9px] font-mono px-2 py-0.5 rounded-full font-semibold ${
                    isClinical
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs'
                      : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/30 shadow-sm'
                  }`}>
                    Official Locked
                  </span>
                </div>

                <div className="mt-1.5 flex items-baseline space-x-2">
                  <div className={`text-2xl sm:text-3xl lg:text-4xl font-extrabold font-mono tracking-tight ${
                    isClinical ? 'text-slate-900' : 'text-emerald-200'
                  }`}>
                    ${data.summary.calculatedTotalPay.toFixed(2)}
                  </div>
                  {isFinalized && (
                    <span className={`text-[11px] font-normal ${
                      isClinical ? 'text-slate-600' : 'text-slate-400'
                    }`}>
                      locked payout
                    </span>
                  )}
                </div>

                {/* Sub-breakdown footer strip */}
                <div className={`mt-2.5 pt-2 border-t flex flex-wrap items-center justify-between gap-1.5 text-[11px] ${
                  isClinical ? 'border-slate-100' : 'border-emerald-500/20'
                }`}>
                  <div className={isClinical ? 'text-slate-800' : 'text-slate-400'}>
                    Base: <strong className={`font-mono ${isClinical ? 'text-slate-900' : 'text-emerald-300'}`}>${data.summary.totalProratedPay.toFixed(2)}</strong>
                  </div>
                  {data.summary.totalManualAdjustments !== 0 && (
                    <div className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      Adj: {data.summary.totalManualAdjustments > 0 ? '+' : ''}${data.summary.totalManualAdjustments.toFixed(2)}
                    </div>
                  )}
                  <div className={`font-mono ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                    <span className={isClinical ? 'text-emerald-700 font-semibold' : 'text-emerald-400 font-medium'}>{clearedCount} Cleared</span> • <span>{pendingCount} Pending</span>
                  </div>
                </div>
              </div>
              )}

              {/* HERO 2: Completed Services */}
              <div className={`rounded-xl px-4 py-3 relative overflow-hidden transition-all ${
                isClinical
                  ? 'bg-white border border-slate-200/90 shadow-2xs'
                  : 'bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-950/95 border border-cyan-500/25 shadow-black/40 hover:border-cyan-500/40'
              }`}>
                {!isClinical && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-teal-500/80 via-sky-400/80 to-teal-500/80" />
                    <div className="absolute top-0 right-0 w-28 h-28 bg-sky-500/5 rounded-full blur-2xl pointer-events-none" />
                  </>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`p-1 rounded-md ${
                      isClinical
                        ? 'bg-slate-100 border border-slate-200 text-slate-700'
                        : 'bg-sky-500/15 border border-sky-500/30 text-sky-400'
                    }`}>
                      <FileCheck className="w-3.5 h-3.5" />
                    </div>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${
                      isClinical ? 'text-slate-900' : 'text-slate-300'
                    }`}>
                      Completed Services
                    </span>
                  </div>
                  <span className={`text-[9px] font-mono px-2 py-0.5 rounded-full font-semibold ${
                    isClinical
                      ? 'bg-slate-100 text-slate-700 border border-slate-200'
                      : 'bg-slate-800 text-slate-300 border border-slate-700'
                  }`}>
                    14-Day Cycle
                  </span>
                </div>

                <div className="mt-1.5 flex items-baseline space-x-2">
                  <div className={`text-2xl sm:text-3xl lg:text-4xl font-extrabold font-mono tracking-tight ${
                    isClinical ? 'text-slate-900' : 'text-white'
                  }`}>
                    {data.summary.completedServiceCount}
                  </div>
                  <span className={`text-[11px] font-medium ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>resolved encounters</span>
                </div>

              </div>
            </div>

            {/* SECONDARY METRICS STRIP: Clinical Minutes & Adjustments — Slim Ledger Line */}
            <div className={`border rounded-lg px-3.5 py-1.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px] ${
              isClinical
                ? 'bg-slate-50/80 border-slate-200/90 shadow-2xs text-slate-700'
                : 'bg-slate-950/80 border border-slate-800/90 shadow-inner text-slate-400'
            }`}>
              {/* Clinical Documentation Minutes */}
              <div className={`flex items-center space-x-2 ${isClinical ? 'text-slate-700' : 'text-slate-400'}`}>
                <Clock className={`w-3 h-3 shrink-0 ${isClinical ? 'text-slate-600' : 'text-cyan-400'}`} />
                <div className="flex items-baseline space-x-1.5">
                  <span className={isClinical ? 'text-slate-800' : 'text-slate-400'}>Clinical Minutes:</span>
                  <span className={`font-mono font-bold ${isClinical ? 'text-slate-900' : 'text-slate-200'}`}>
                    {data.summary.totalActualMinutes} min
                  </span>
                  <span className={`text-[10px] font-mono ${isClinical ? 'text-slate-600' : 'text-slate-500'}`}>
                    ({data.summary.totalPlannedMinutes} planned)
                  </span>
                </div>
              </div>

              {isFinalized && (
              <div className={`flex items-center space-x-2 border-t sm:border-t-0 pt-1 sm:pt-0 ${
                isClinical ? 'text-slate-700 border-slate-200' : 'text-slate-400 border-slate-800/60'
              }`}>
                <TrendingUp className="w-3 h-3 text-amber-500 shrink-0" />
                <div className="flex items-baseline space-x-1.5">
                  <span className={isClinical ? 'text-slate-800' : 'text-slate-500'}>Adjustments:</span>
                  {data.summary.totalManualAdjustments !== 0 ? (
                    <span className={`font-mono font-bold ${isClinical ? 'text-amber-700' : 'text-amber-300'}`}>
                      {data.summary.totalManualAdjustments > 0 ? '+' : ''}${data.summary.totalManualAdjustments.toFixed(2)}
                    </span>
                  ) : (
                    <span className={`font-mono ${isClinical ? 'text-slate-600' : 'text-slate-500'}`}>$0.00 (none)</span>
                  )}
                  {data.summary.lateExceptionsCount > 0 && (
                    <span className="ml-1 text-[9px] font-mono px-1.5 py-0.2 rounded bg-purple-950 text-purple-300 border border-purple-500/30">
                      {data.summary.lateExceptionsCount} Late Exception
                    </span>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        )}






        {/* ========================================================================= */}
        {/* ENCOUNTER DETAILS TABLE / CARDS (Preserved & Polished)                    */}
        {/* ========================================================================= */}
        {data && (
          <div className={`border rounded-2xl overflow-hidden ${
            isClinical
              ? 'bg-white border-slate-200/90 shadow-2xs'
              : 'bg-gradient-to-b from-[#18263c] via-[#141f32] to-[#101929] border border-slate-700/80 shadow-black/20'
          }`}>
            {/* Table Controls & Filter Header */}
            <div className={`p-4 sm:p-5 border-b flex flex-col md:flex-row md:items-center justify-between gap-4 ${
              isClinical
                ? 'bg-slate-50/90 border-slate-200/90'
                : 'bg-gradient-to-r from-[#20314c] via-[#1a283e] to-[#141f32] border-slate-700/80'
            }`}>
              {/* Mobile Title (sm:hidden) — Centered Encounter Details, count badge, and tightened subtext */}
              <div className="sm:hidden flex flex-col items-center justify-center text-center w-full">
                <h2 className={`text-sm font-bold flex items-center justify-center space-x-2 ${
                  isClinical ? 'text-slate-900' : 'text-white'
                }`}>
                  <span>Encounter Details</span>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-mono font-semibold border ${
                    isClinical
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/30'
                  }`}>
                    {filteredServices.length} of {data.services.length}
                  </span>
                </h2>
                <p className={`text-xs mt-0.5 ${isClinical ? 'text-slate-800' : 'text-slate-400'}`}>
                  Visits resolved by Allegiant payroll engine
                </p>
                {data.provider?.id && (
                  <button
                    type="button"
                    onClick={openClientNcns}
                    className={`mt-1 text-xs font-medium underline-offset-2 hover:underline ${isClinical ? 'text-slate-500' : 'text-slate-400'}`}
                  >
                    Report Client No-Show
                  </button>
                )}
              </div>

              {/* Desktop/Tablet Title (hidden sm:block) */}
              <div className="hidden sm:block">
                <h2 className={`text-sm font-bold flex items-center space-x-2 ${isClinical ? 'text-slate-900' : 'text-white'}`}>
                  <span>Encounter Details</span>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-mono font-semibold border ${
                    isClinical
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/30'
                  }`}>
                    {filteredServices.length} of {data.services.length}
                  </span>
                </h2>
                <p className={`text-xs mt-0.5 ${isClinical ? 'text-slate-800' : 'text-slate-400'}`}>
                  Itemized clinical encounters resolved by the Allegiant payroll engine
                </p>
                {data.provider?.id && (
                  <button
                    type="button"
                    onClick={openClientNcns}
                    className={`mt-1 text-xs font-medium underline-offset-2 hover:underline ${isClinical ? 'text-slate-500' : 'text-slate-400'}`}
                  >
                    Report Client No-Show
                  </button>
                )}
              </div>

              {/* Filters Bar: Mobile reflow (sm:hidden) vs Desktop/Tablet (hidden sm:flex) */}
              {/* Mobile Filters: Row 1 = Search + Status dropdown | Row 2 = All / W1 / W2 full-width chips */}
              <div className="sm:hidden flex flex-col gap-2 w-full pt-1">
                {/* Row 1: [ Search records... ] [ All Statuses ] */}
                <div className="flex items-center gap-2 w-full">
                  <div className="relative flex-1 min-w-0">
                    <Search className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                      isClinical ? 'text-slate-500' : 'text-slate-400'
                    }`} />
                    <input
                      type="text"
                      placeholder="Search records..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`rounded-xl pl-8 pr-2.5 py-1.5 text-xs focus:outline-none w-full ${
                        isClinical
                          ? 'bg-white border border-slate-200 text-slate-800 placeholder-slate-500 focus:border-slate-400 shadow-2xs'
                          : 'bg-[#0e1626] border border-slate-700/70 text-slate-200 placeholder-slate-500 focus:border-emerald-500 shadow-inner'
                      }`}
                    />
                  </div>
                  <div className="shrink-0 w-36">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className={`text-xs rounded-xl px-2.5 py-1.5 focus:outline-none cursor-pointer w-full ${
                        isClinical
                          ? 'bg-white border border-slate-200 text-slate-800 focus:border-slate-400 shadow-2xs'
                          : 'bg-[#0e1626] border border-slate-700/70 text-slate-300 focus:border-emerald-500 shadow-inner'
                      }`}
                    >
                      <option value="ALL">All Statuses</option>
                      <option value="CLEARED">Cleared / Finalized</option>
                      <option value="PENDING_SUPERVISOR">Pending Supervisor</option>
                      <option value="PENDING_GUARDIAN">Pending Guardian</option>
                      <option value="COMPLIANCE">Compliance Rate</option>
                      <option value="LATE">Late Exceptions</option>
                    </select>
                  </div>
                </div>

                {/* Row 2: [ All (X) ] [ W1 (Y) ] [ W2 (Z) ] spread balanced across row */}
                <div className={`grid grid-cols-3 rounded-xl p-1 text-xs w-full ${
                  isClinical
                    ? 'bg-slate-100/90 border border-slate-200 shadow-2xs'
                    : 'bg-[#0e1626] border border-slate-700/70 shadow-inner'
                }`}>
                  <button
                    onClick={() => setSelectedWeekFilter('ALL')}
                    className={`py-1 rounded-lg font-medium transition-colors cursor-pointer text-center ${
                      selectedWeekFilter === 'ALL'
                        ? isClinical
                          ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-2xs'
                          : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : isClinical
                        ? 'text-slate-700 hover:text-slate-900'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    All ({data.services.length})
                  </button>
                  <button
                    onClick={() => setSelectedWeekFilter('WEEK1')}
                    className={`py-1 rounded-lg font-medium transition-colors cursor-pointer text-center ${
                      selectedWeekFilter === 'WEEK1'
                        ? isClinical
                          ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-2xs'
                          : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : isClinical
                        ? 'text-slate-700 hover:text-slate-900'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    W1 ({week1Count})
                  </button>
                  <button
                    onClick={() => setSelectedWeekFilter('WEEK2')}
                    className={`py-1 rounded-lg font-medium transition-colors cursor-pointer text-center ${
                      selectedWeekFilter === 'WEEK2'
                        ? isClinical
                          ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-2xs'
                          : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : isClinical
                        ? 'text-slate-700 hover:text-slate-900'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    W2 ({week2Count})
                  </button>
                </div>
              </div>

              {/* Desktop Filters Bar (hidden sm:flex) */}
              <div className="hidden sm:flex flex-wrap items-center gap-2.5">
                {/* Search */}
                <div className="relative">
                  <Search className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 ${isClinical ? 'text-slate-500' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    placeholder="Search records..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`rounded-xl pl-8 pr-3 py-1.5 text-xs focus:outline-none w-44 sm:w-56 ${
                      isClinical
                        ? 'bg-white border border-slate-200 text-slate-800 placeholder-slate-500 focus:border-slate-400 shadow-2xs'
                        : 'bg-[#0e1626] border border-slate-700/70 text-slate-200 placeholder-slate-500 focus:border-emerald-500 shadow-inner'
                    }`}
                  />
                </div>

                {/* Week Filter Pills */}
                <div className={`flex rounded-xl p-1 text-xs ${
                  isClinical ? 'bg-slate-100 border border-slate-200 shadow-2xs' : 'bg-[#0e1626] border border-slate-700/70 shadow-inner'
                }`}>
                  <button
                    onClick={() => setSelectedWeekFilter('ALL')}
                    className={`px-2.5 py-0.5 rounded-lg font-medium transition-colors cursor-pointer ${
                      selectedWeekFilter === 'ALL'
                        ? isClinical
                          ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-2xs'
                          : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : isClinical
                        ? 'text-slate-700 hover:text-slate-900'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    All ({data.services.length})
                  </button>
                  <button
                    onClick={() => setSelectedWeekFilter('WEEK1')}
                    className={`px-2.5 py-0.5 rounded-lg font-medium transition-colors cursor-pointer ${
                      selectedWeekFilter === 'WEEK1'
                        ? isClinical
                          ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-2xs'
                          : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : isClinical
                        ? 'text-slate-700 hover:text-slate-900'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    W1 ({week1Count})
                  </button>
                  <button
                    onClick={() => setSelectedWeekFilter('WEEK2')}
                    className={`px-2.5 py-0.5 rounded-lg font-medium transition-colors cursor-pointer ${
                      selectedWeekFilter === 'WEEK2'
                        ? isClinical
                          ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-2xs'
                          : 'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : isClinical
                        ? 'text-slate-700 hover:text-slate-900'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    W2 ({week2Count})
                  </button>
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`text-xs rounded-xl px-3 py-1.5 focus:outline-none cursor-pointer ${
                    isClinical
                      ? 'bg-white border border-slate-200 text-slate-800 focus:border-slate-400 shadow-2xs'
                      : 'bg-[#0e1626] border border-slate-700/70 text-slate-300 focus:border-emerald-500 shadow-inner'
                  }`}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="CLEARED">Cleared / Finalized</option>
                  <option value="PENDING_SUPERVISOR">Pending Supervisor</option>
                  <option value="PENDING_GUARDIAN">Pending Guardian</option>
                  <option value="COMPLIANCE">Compliance Rate</option>
                  <option value="LATE">Late Exceptions</option>
                </select>
              </div>
            </div>

            {/* Encounters Content */}
            {loading ? (
              <div className={`p-12 text-center text-xs ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                <div className="inline-block w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-2" />
                <p>Loading encounter payroll details...</p>
              </div>
            ) : filteredServices.length === 0 ? (
              <div className={`p-12 text-center text-xs ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                <FileCheck className={`w-8 h-8 mx-auto mb-2 ${isClinical ? 'text-slate-500' : 'text-slate-400'}`} />
                <p className={`font-semibold ${isClinical ? 'text-slate-800' : 'text-slate-300'}`}>No encounters match your filter criteria.</p>
                <p className={`text-[11px] mt-1 ${isClinical ? 'text-slate-600' : 'text-slate-500'}`}>
                  {data.services.length === 0
                    ? 'No completed services found for this pay period.'
                    : 'Try clearing your search or status filter.'}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop View Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className={`${
                        isClinical
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-950/70 border-b border-slate-800 text-slate-400'
                      } text-[11px] font-semibold uppercase tracking-wider`}>
                        <th className="p-3.5">DOS</th>
                        <th className="p-3.5">Week</th>
                        <th className="p-3.5">Client</th>
                        <th className="p-3.5">Service / CPT</th>
                        <th className="p-3.5">Modality</th>
                        <th className="p-3.5">Duration</th>
                        <th className="p-3.5">Clearance Status</th>
                        {isFinalized && <th className="p-3.5">Pay</th>}
                      </tr>
                    </thead>
                    <tbody className={isClinical ? 'divide-y divide-slate-100 bg-white' : 'divide-y divide-slate-800/60'}>
                      {filteredServices.map((srv, srvIdx) => (
                        <tr
                          key={srv.id}
                          className={`transition-colors ${
                            isClinical
                              ? srvIdx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100/70'
                              : `${srvIdx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'} hover:bg-[#20314a]`
                          }`}
                        >
                          {/* DOS */}
                          <td className={`p-3.5 font-mono whitespace-nowrap ${isClinical ? 'text-slate-700' : 'text-slate-300'}`}>
                            {srv.dos}
                            {srv.carriedFromEarlierPeriod && (
                              <div className={`mt-1 max-w-[140px] whitespace-normal text-[10px] font-sans leading-snug ${isClinical ? 'text-cyan-800' : 'text-cyan-200'}`}>
                                {srv.heldFromLabel || `Held from ${srv.heldFromPeriodLabel || 'an earlier period'}`}
                              </div>
                            )}
                          </td>

                          {/* Week */}
                          <td className="p-3.5 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${
                              isClinical ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-slate-950 text-slate-300 border-slate-800'
                            }`}>
                              W{srv.weekNumber}
                            </span>
                          </td>

                          {/* Client */}
                          <td className="p-3.5">
                            <div className={`font-semibold ${isClinical ? 'text-slate-900' : 'text-white'}`}>
                              {onSelectClient ? (
                                <button
                                  onClick={() => onSelectClient(srv.clientId)}
                                  className="hover:underline transition-colors text-left"
                                >
                                  {srv.clientName}
                                </button>
                              ) : (
                                srv.clientName
                              )}
                            </div>
                            <div className={`text-[10px] font-mono ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                              ID: {srv.ahcccsId || srv.clientId}
                            </div>
                          </td>

                          {/* Service Code & Type */}
                          <td className="p-3.5 whitespace-nowrap">
                            {srv.isClientNcns ? (
                              <span className={`font-semibold ${isClinical ? 'text-slate-800' : 'text-slate-200'}`}>
                                {clientNcnsEncounterLabel(srv.calculatedTotal ?? 0)}
                              </span>
                            ) : (
                              <>
                                <span className={`font-mono font-semibold ${isClinical ? 'text-indigo-700' : 'text-sky-300'}`}>
                                  {srv.serviceCode}
                                </span>
                                <span className={`text-[10px] ml-1.5 ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                                  ({srv.serviceType})
                                </span>
                              </>
                            )}
                          </td>

                          {/* Modality */}
                          <td className="p-3.5 whitespace-nowrap">
                            {srv.isClientNcns ? (
                              <span className={isClinical ? 'text-slate-400' : 'text-slate-500'}>—</span>
                            ) : (
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                  srv.modality === 'ONSITE'
                                    ? isClinical
                                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                      : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                                    : isClinical
                                    ? 'bg-sky-50 text-sky-800 border-sky-200'
                                    : 'bg-sky-500/10 text-sky-300 border border-sky-500/20'
                                }`}
                              >
                                {srv.modality}
                              </span>
                            )}
                          </td>

                          {/* Duration */}
                          <td className={`p-3.5 whitespace-nowrap font-mono ${isClinical ? 'text-slate-700' : 'text-slate-300'}`}>
                            {srv.isClientNcns ? (
                              <span className={isClinical ? 'text-slate-400' : 'text-slate-500'}>—</span>
                            ) : (
                              <>
                                <span>{srv.actualDurationMinutes}m</span>
                                {srv.plannedDurationMinutes !== srv.actualDurationMinutes && (
                                  <span className={`text-[10px] ml-1 ${isClinical ? 'text-slate-600' : 'text-slate-500'}`}>
                                    (pl: {srv.plannedDurationMinutes}m)
                                  </span>
                                )}
                                {srv.isUnder53Minutes && (
                                  <span className={`block text-[10px] ${isClinical ? 'text-amber-700 font-semibold' : 'text-amber-400'}`}>
                                    &lt;53m visit
                                  </span>
                                )}
                              </>
                            )}
                          </td>

                          {/* Clearance Status */}
                          <td className="p-3.5 whitespace-nowrap">
                            {srv.isClientNcns ? (
                              <span className={isClinical ? 'text-slate-400' : 'text-slate-500'}>—</span>
                            ) : renderClearanceBadge(srv)}
                            {srv.documentationEligible === false && (
                              <div className={`mt-1 text-[10px] font-semibold ${isClinical ? 'text-cyan-800' : 'text-indigo-300'}`} title={srv.documentationHoldReason || 'Not included in payable total'}>
                                Excluded
                              </div>
                            )}
                            {srv.documentationEligible !== false && srv.calculatedTotal === null && !srv.isClientNcns && (
                              <div className={`mt-1 text-[10px] font-semibold ${isClinical ? 'text-purple-700' : 'text-purple-400'}`}>
                                Review Req.
                              </div>
                            )}
                            {srv.documentationEligible === false && srv.documentationHoldReason && (
                              <div className={`mt-1 max-w-[240px] whitespace-normal text-[10px] leading-snug ${isClinical ? 'text-slate-600' : 'text-slate-400'}`} title={srv.documentationHoldReason}>
                                {srv.documentationHoldReason}
                              </div>
                            )}
                          </td>
                          {isFinalized && (
                            <td className={`p-3.5 whitespace-nowrap font-mono font-semibold ${isClinical ? 'text-slate-900' : 'text-emerald-200'}`}>
                              {srv.calculatedTotal == null ? '—' : `$${srv.calculatedTotal.toFixed(2)}`}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Tablet / Mobile Stacked Cards Layout */}
                <div className="lg:hidden p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                  {filteredServices.map((srv, idx) => (
                    <div
                      key={srv.id}
                      className={`rounded-xl p-3.5 sm:p-4 space-y-3 transition-all ${
                        isClinical
                          ? 'bg-white hover:bg-slate-50/80 border border-slate-200/90 shadow-2xs'
                          : `${idx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'} border border-slate-700/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_2px_8px_rgba(0,0,0,0.3)] hover:border-emerald-500/50 hover:bg-[#20314a]`
                      }`}
                    >
                      {/* Top row: Client & Net Pay */}
                      <div className="flex items-start justify-between">
                        <div>
                          <div className={`font-bold text-sm ${isClinical ? 'text-slate-900' : 'text-white'}`}>
                            {srv.clientName}
                          </div>
                          <div className={`text-[10px] font-mono ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                            AHCCCS: {srv.ahcccsId || srv.clientId}
                          </div>
                        </div>
                        <div className="text-right">
                          {srv.documentationEligible === false ? (
                            <div className={`font-bold font-mono text-xs ${isClinical ? 'text-cyan-800' : 'text-indigo-300'}`} title={srv.documentationHoldReason || 'Not included in payable total'}>
                              Excluded
                            </div>
                          ) : srv.calculatedTotal === null ? (
                            <div className={`font-bold font-mono text-xs ${isClinical ? 'text-purple-700' : 'text-purple-400'}`}>
                              Review Req.
                            </div>
                          ) : isFinalized ? (
                            <div className={`font-bold font-mono text-sm ${isClinical ? 'text-slate-900' : 'text-emerald-200'}`}>
                              ${srv.calculatedTotal.toFixed(2)}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {srv.isClientNcns ? (
                        <div>
                          <div className={`text-sm font-semibold ${isClinical ? 'text-slate-800' : 'text-slate-200'}`}>
                            {clientNcnsEncounterLabel(srv.calculatedTotal ?? 0)}
                          </div>
                          <div className={`mt-1 font-mono text-xs ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                            {srv.dos}
                          </div>
                        </div>
                      ) : (
                      <>
                      {/* Middle metadata chips */}
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className={`font-mono px-2.5 py-0.5 rounded-full border ${
                          isClinical
                            ? 'text-slate-700 bg-slate-50 border-slate-200'
                            : 'text-slate-300 bg-[#121c2d] border-slate-700/80'
                        }`}>
                          {srv.dos} (W{srv.weekNumber})
                          {srv.carriedFromEarlierPeriod ? ` · ${srv.heldFromLabel || 'Held from an earlier period'}` : ''}
                        </span>
                        <span className={`font-mono font-semibold px-2.5 py-0.5 rounded-full border ${
                          isClinical
                            ? 'text-indigo-800 bg-indigo-50 border-indigo-200'
                            : 'text-sky-300 bg-sky-950/60 border-sky-800/40'
                        }`}>
                          CPT {srv.serviceCode}
                        </span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full font-semibold border ${
                            srv.modality === 'ONSITE'
                              ? isClinical
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                              : isClinical
                              ? 'bg-sky-50 text-sky-800 border-sky-200'
                              : 'bg-sky-500/10 text-sky-300 border border-sky-500/20'
                          }`}
                        >
                          {srv.modality}
                        </span>
                        <span className={`font-mono px-2.5 py-0.5 rounded-full border ${
                          isClinical
                            ? 'text-slate-600 bg-slate-50 border-slate-200'
                            : 'text-slate-400 bg-[#121c2d] border-slate-700/80'
                        }`}>
                          {srv.actualDurationMinutes}m
                        </span>
                      </div>

                      {/* Bottom status */}
                      <div className={`flex items-center justify-end pt-2 border-t ${
                        isClinical ? 'border-slate-200' : 'border-slate-800/80'
                      }`}>
                        <div className="text-right">
                          {renderClearanceBadge(srv)}
                          {srv.documentationEligible === false && srv.documentationHoldReason && (
                            <div className={`mt-1 max-w-[220px] text-[10px] leading-snug ${isClinical ? 'text-slate-600' : 'text-slate-400'}`}>
                              {srv.documentationHoldReason}
                            </div>
                          )}
                        </div>
                      </div>
                      </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {ncnsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => { if (!ncnsSaving) setNcnsOpen(false); }}>
          <form
            onClick={(event) => event.stopPropagation()}
            onSubmit={submitClientNcns}
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
          >
            <h3 className="text-sm font-bold text-slate-900">Report Client No-Show</h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">
              Use this when you arrived and the client had not cancelled before you got there.
            </p>
            <label className="mt-4 block text-xs font-semibold text-slate-700">
              Client
              <select
                value={ncnsClientId}
                onChange={(event) => setNcnsClientId(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                required
              >
                <option value="">Select a client</option>
                {ncnsClients.map((assignment) => (
                  <option key={assignment.client_id} value={assignment.client_id}>
                    {`${assignment.client_last_name || ''}${assignment.client_last_name ? ', ' : ''}${assignment.client_first_name || assignment.client_id}`.trim()}
                  </option>
                ))}
              </select>
            </label>
            {ncnsClients.length === 0 && (
              <p className="mt-2 text-xs text-slate-500">No active clients are assigned to you.</p>
            )}
            <label className="mt-3 block text-xs font-semibold text-slate-700">
              Date of Service
              <input
                type="date"
                value={ncnsDos}
                onChange={(event) => setNcnsDos(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900"
                required
              />
            </label>
            <p className="mt-3 text-xs text-slate-500">Recording as {data?.provider?.name || 'your provider profile'}.</p>
            {ncnsError && <p className="mt-3 text-xs text-rose-700">{ncnsError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setNcnsOpen(false)}
                disabled={ncnsSaving}
                className="rounded-lg px-3 py-2 text-xs font-semibold text-slate-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={ncnsSaving || !ncnsClientId}
                className="rounded-lg bg-slate-800 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
              >
                {ncnsSaving ? 'Saving' : 'Record No-Show'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

